import { React, useState, useEffect } from 'react';
import {
  Button, Select, MenuItem, Stack, Checkbox, ListItemText, FormControl, InputLabel, OutlinedInput, FormHelperText,
} from '@mui/material';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { createTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { startDataCollection, stopDataCollection, fetchDeviceOptions } from '../../services/remote';
import { useDataCollection } from '../../helpers/dataCollectionContext';
import { getCurrentUserId } from '../../helpers/auth';
import Modal from '../../components/Modal/Modal';
import './Hardware.css';

const SUIT_PRESSURE_ANNOUNCEMENT_KEY = 'hippo:suit-pressure-announced';

const getAnnouncementKey = () => `${SUIT_PRESSURE_ANNOUNCEMENT_KEY}:${getCurrentUserId()}`;

const ARGOS_DEVICE_PATTERN = /argos|sseo/;

export default function Hardware({ setError }) {
  const [deviceOptions, setDeviceOptions] = useState([]);
  const sensorOptions = ['Polar', 'Vaisala', 'Kurz'];
  const evOptions = ['EV1', 'EV2'];
  const suitPressureOptions = ['4.3', '5.2'];

  const [showAnnouncement, setShowAnnouncement] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(getAnnouncementKey())) {
        setShowAnnouncement(true);
      }
    } catch {
      // ignore storage access errors
    }
  }, []);

  const dismissAnnouncement = () => {
    try {
      localStorage.setItem(getAnnouncementKey(), 'true');
    } catch {
      // ignore storage access errors
    }
    setShowAnnouncement(false);
  };

  useEffect(() => {
    const loadDeviceOptions = async () => {
      try {
        const options = await fetchDeviceOptions();
        setDeviceOptions(Array.isArray(options) ? options : []);
      } catch (error) {
        setError(error.message);
      }
    };

    loadDeviceOptions();
  }, [setError]);

  const argosDeviceOptions = (deviceOptions || []).filter(
    (option) => ARGOS_DEVICE_PATTERN.test(option),
  );

  const theme = createTheme({
    palette: {
      gray: {
        main: 'rgba(0, 0, 0, 0.6)',
        light: 'rgba(0, 0, 0, 0.23)',
      },
    },
  });

  const [dropdownGroups, setDropdownGroups] = useState([
    {
      id: 1, device: '', sensors: [], ev: '', suitPressure: '',
    },
  ]);

  const addDropdownGroup = () => {
    const newId = Math.max(...dropdownGroups.map((group) => group.id)) + 1;
    setDropdownGroups([...dropdownGroups, {
      id: newId, device: '', sensors: [], ev: '', suitPressure: '',
    }]);
  };

  const removeDropdownGroup = (id) => {
    if (dropdownGroups.length > 1) {
      const groupToRemove = dropdownGroups.find((group) => group.id === id);
      if (!groupToRemove.inUse) {
        setDropdownGroups(dropdownGroups.filter((group) => group.id !== id));
      }
    }
  };

  const [errors, setErrors] = useState({});

  const handleSelection = (id, type, value) => {
    setDropdownGroups(dropdownGroups.map((group) => (group.id === id ? { ...group, [type]: value } : group)));
    setErrors((prev) => ({ ...prev, [`${type}-${id}`]: null }));
  };

  const validateSelections = () => {
    const newErrors = {};
    let isValid = true;

    dropdownGroups.forEach((group) => {
      if (!group.device) {
        newErrors[`device-${group.id}`] = 'Select an option';
        isValid = false;
      }
      if (group.sensors.length === 0) {
        newErrors[`sensors-${group.id}`] = 'Select an option';
        isValid = false;
      }
      if (!group.ev) {
        newErrors[`ev-${group.id}`] = 'Select an option';
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const [isLoading, setIsLoading] = useState(false);
  const [trialId, setTrialId] = useState(null);
  const { activeDevices, updateActiveDevices } = useDataCollection();

  useEffect(() => {
    setDropdownGroups((prevGroups) => prevGroups.map((group) => {
      const activeDevice = (activeDevices || []).find((device) => device.thing_name === group.device);
      return activeDevice ? { ...group, inUse: true } : group;
    }));
  }, [activeDevices]);

  const handleStartData = async () => {
    if (validateSelections()) {
      setIsLoading(true);
      try {
        const result = await startDataCollection(dropdownGroups);
        updateActiveDevices(result.devices || []);
        setTrialId(result.trial_id);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleStopData = async () => {
    if (activeDevices.length > 0 && trialId) {
      setIsLoading(true);
      try {
        await stopDataCollection(activeDevices, trialId);
        updateActiveDevices([]);
        setTrialId(null);
        setDropdownGroups((prevGroups) => prevGroups.map((group) => ({ ...group, inUse: false }))); // reset dropdown-groups to be non-active again
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const renderGroupAction = (group) => {
    if (group.inUse) {
      return <div className="in-use-label">In Use</div>;
    } if (dropdownGroups.length > 1) {
      return (
        <Button
          variant="outlined"
          size="medium"
          endIcon={<DeleteIcon />}
          onClick={() => removeDropdownGroup(group.id)}
          sx={{
            color: theme.palette.gray.main,
            borderColor: theme.palette.gray.light,
            '&:hover': {
              color: theme.palette.gray.main,
              borderColor: theme.palette.gray.light,
              backgroundColor: theme.palette.gray.light,
            },
          }}
        >
          Remove
        </Button>
      );
    }
    return null;
  };

  const announcementBody = (
    <div className="suit-pressure-announcement">
      <p>
        You can now select a Suit Pressure (psid) when starting data collection.
        The selected pressure drives the metabolic-rate calculation and appears as a
        label on the Met Rate plot. It only applies to the ARGOS location; if you
        don&apos;t select a pressure, met rate is computed exactly as before.
      </p>
      <Button variant="contained" color="primary" onClick={dismissAnnouncement}>
        Got it
      </Button>
    </div>
  );

  return (
    <div className="hardware-page-container">
      <Modal
        isShowing={showAnnouncement}
        hide={dismissAnnouncement}
        title="New: Dynamic Suit Pressure"
        innerComponent={announcementBody}
      />
      <div className="hardware-page-main-content">
        <section className="device-control">
          <div className="control-info">
            <h2 className="section-title">Device Remote Start</h2>
            <p className="section-description">
              Initiate data collection by selecting the desired devices and associated EV.
              <br />
              Data collection won&apos;t start until you select &apos;Start Data&apos;.
            </p>
          </div>
          <div className="control-actions">
            <Stack direction="row" spacing={2}>
              <Button
                size="large"
                sx={{ maxHeight: 54, textTransform: 'none' }}
                variant="outlined"
                color="error"
                startIcon={<StopIcon />}
                onClick={handleStopData}
                disabled={isLoading || activeDevices.length === 0}
              >
                Stop Data
              </Button>
              <Button
                size="large"
                sx={{ maxHeight: 54, color: '2B6ED3', textTransform: 'none' }}
                variant="contained"
                color="primary"
                endIcon={<PlayArrowIcon />}
                onClick={handleStartData}
                disabled={isLoading || activeDevices.length > 0}
              >
                {isLoading ? 'Starting' : 'Start Data'}
              </Button>
            </Stack>
          </div>
        </section>

        <section className="device-selection">
          <h2 className="section-title">Devices</h2>
          <div className="selection-container">
            {dropdownGroups.map((group) => (
              <div className="dropdown-group" key={group.id}>
                <div className="dropdown-group-select-group">
                  <FormControl
                    sx={{ minWidth: '200px' }}
                    size="small"
                    error={!!errors[`device-${group.id}`]}
                  >
                    <InputLabel id={`select-device-label-${group.id}`}>Select Device</InputLabel>
                    <Select
                      labelId={`select-device-label-${group.id}`}
                      id={`select-device-${group.id}`}
                      value={group.device}
                      onChange={(e) => handleSelection(group.id, 'device', e.target.value)}
                      input={<OutlinedInput label="Select Device" />}
                      autoWidth
                    >
                      {argosDeviceOptions.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                    {errors[`device-${group.id}`] && (
                      <FormHelperText sx={{ margin: 0, gap: 0 }}>{errors[`device-${group.id}`]}</FormHelperText>
                    )}
                  </FormControl>

                  <FormControl
                    sx={{ minWidth: '200px' }}
                    size="small"
                    error={!!errors[`sensors-${group.id}`]}
                  >
                    <InputLabel id={`select-sensors-label-${group.id}`}>Select Sensors</InputLabel>
                    <Select
                      labelId={`select-sensors-label-${group.id}`}
                      id={`select-sensors-${group.id}`}
                      multiple
                      value={group.sensors}
                      onChange={(e) => handleSelection(group.id, 'sensors', e.target.value)}
                      renderValue={(selected) => selected.join(', ')}
                      input={<OutlinedInput label="Select Sensors" />}
                      autoWidth
                    >
                      {sensorOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          <Checkbox checked={group.sensors.indexOf(option) > -1} />
                          <ListItemText primary={option} />
                        </MenuItem>
                      ))}
                    </Select>
                    {errors[`sensors-${group.id}`] && (
                      <FormHelperText>{errors[`sensors-${group.id}`]}</FormHelperText>
                    )}
                  </FormControl>

                  <FormControl
                    sx={{ minWidth: '200px' }}
                    size="small"
                    error={!!errors[`ev-${group.id}`]}
                  >
                    <InputLabel id={`select-ev-label-${group.id}`}>Select EV</InputLabel>
                    <Select
                      labelId={`select-ev-label-${group.id}`}
                      id={`select-ev-${group.id}`}
                      value={group.ev}
                      onChange={(e) => handleSelection(group.id, 'ev', e.target.value)}
                      input={<OutlinedInput label="Select EV" />}
                      autoWidth
                    >
                      <MenuItem value="" disabled>Select EV</MenuItem>
                      {evOptions.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                    {errors[`ev-${group.id}`] && (
                      <FormHelperText>{errors[`ev-${group.id}`]}</FormHelperText>
                    )}
                  </FormControl>
                  <FormControl
                    sx={{ minWidth: '200px' }}
                    size="small"
                    error={!!errors[`suitPressure-${group.id}`]}
                  >
                    <InputLabel id={`select-suit-pressure-label-${group.id}`}>Suit Pressure (psid)</InputLabel>
                    <Select
                      labelId={`select-suit-pressure-label-${group.id}`}
                      id={`select-suit-pressure-${group.id}`}
                      value={group.suitPressure}
                      onChange={(e) => handleSelection(group.id, 'suitPressure', e.target.value)}
                      input={<OutlinedInput label="Suit Pressure (psid)" />}
                      autoWidth
                    >
                      <MenuItem value="" disabled>Suit Pressure (psid)</MenuItem>
                      {suitPressureOptions.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                    {errors[`suitPressure-${group.id}`] && (
                      <FormHelperText>{errors[`suitPressure-${group.id}`]}</FormHelperText>
                    )}
                  </FormControl>
                </div>
                <div className="dropdown-group-remove-group">
                  {renderGroupAction(group)}
                </div>
              </div>
            ))}
          </div>
          <div className="add-button-container">
            <Button
              variant="outlined"
              size="medium"
              onClick={addDropdownGroup}
              sx={{
                color: theme.palette.gray.main,
                borderColor: theme.palette.gray.light,
                '&:hover': {
                  color: theme.palette.gray.main,
                  borderColor: theme.palette.gray.light,
                  backgroundColor: theme.palette.gray.light,
                },
              }}
              endIcon={<AddIcon />}
            >
              Add
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}

Hardware.propTypes = {
  setError: PropTypes.func.isRequired,
};
